import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { vslDinamica2News, type NovidadeSection } from '@/data/news/vsl-dinamica-2';
import { NovidadeSections } from '@/components/novidades/NovidadeSections';
import { useState, useMemo, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { Search, Sparkles, Megaphone, X, MessageCircle, ChevronDown, Check } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogTitle,
  DialogDescription,
  DialogPortal,
  DialogOverlay,
} from '@/components/ui/dialog';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import frontV2Banner from '@/assets/novidades/front-v2-banner.jpg';
import picosHorarioBanner from '@/assets/novidades/picos-horario.jpg';
import compartilharVideoBanner from '@/assets/novidades/compartilhar-video-banner-v2.jpg';

interface Novidade {
  id: string;
  type: string;
  badge: string;
  badgeColor: string;
  date: string;
  title: string;
  description: string;
  bullets: string[];
  image?: string;
  footerNote?: string;
  sections?: NovidadeSection[];
}

const NOVIDADES_DATA: Novidade[] = [
  vslDinamica2News,
  {
    id: 'nov-2026-06-03-tempo-real',
    type: 'novo_recurso',
    badge: 'Novo recurso',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-06-03',
    get title() { return translateInterfaceText("Audiência em Tempo Real"); },
    get description() { return translateInterfaceText("Chegou a Audiência em Tempo Real: um mapa-múndi que mostra, ao vivo, quem está assistindo às suas VSLs neste exato momento e de onde no mundo. Acompanhe quantas pessoas estão na sua oferta agora, quais vídeos estão bombando e de quais fontes de tráfego elas vieram — tudo atualizando sozinho."); },
    get bullets() { return [
      translateInterfaceText("Mapa ao vivo com os espectadores que estão assistindo agora, por cidade e país"),
      translateInterfaceText("Contador de \"assistindo agora\" que se atualiza sozinho a cada poucos segundos"),
      translateInterfaceText("Rankings ao vivo: top vídeos, top lugares (com alternância país/estado) e top fontes de tráfego"),
      translateInterfaceText("Conta apenas quem está realmente assistindo com o som ativo — audiência engajada de verdade"),
      translateInterfaceText("Acesse pelo menu lateral em Análises → Tempo Real"),
    ]; },
    get footerNote() { return translateInterfaceText("Já está disponível no menu Análises do seu painel."); },
  },
  {
    id: 'nov-2026-05-19-player-performance',
    type: 'melhoria',
    badge: 'Melhoria',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-05-19',
    get title() { return translateInterfaceText("Novo Player de vídeo da HostVSL"); },
    get description() { return translateInterfaceText("Ei, novidade boa por aqui! A gente trabalhou nos bastidores e entregou uma melhoria de performance completa na abertura dos vídeos. Resultado: carregamento mais rápido, menos travamentos e uma experiência muito melhor pra quem assiste o seu conteúdo."); },
    get bullets() { return [
      translateInterfaceText("Carregamento mais rápido no início do vídeo"),
      translateInterfaceText("Menos travamentos durante a reprodução"),
      translateInterfaceText("Experiência mais fluida para a sua audiência"),
      translateInterfaceText("Pra ativar: copie novamente o código do seu vídeo e cole no lugar do antigo, lá onde ele está hospedado"),
      translateInterfaceText("Pronto! Depois disso, sua audiência já passa a aproveitar todas as melhorias"),
    ]; },
    get footerNote() { return translateInterfaceText("Qualquer dúvida no caminho, é só chamar. Estamos aqui pra ajudar."); },
  },
  {
    id: 'nov-2026-05-19-storyboard-player',
    type: 'novo_recurso',
    badge: 'Novo recurso',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-05-19',
    get title() { return translateInterfaceText("Storyboard"); },
    get description() { return translateInterfaceText("Chegou o Storyboard, um novo recurso dos controles do player. Ao passar o mouse pela barra de progresso, o espectador vê uma miniatura do frame correspondente àquele instante do vídeo. Ajuda a encontrar rapidamente um trecho específico ou voltar pra um ponto de interesse."); },
    get bullets() { return [
      translateInterfaceText("Mini-preview do frame ao passar o mouse na barra de progresso"),
      translateInterfaceText("Ative na aba \"Controles\" da personalização do vídeo, na opção \"Preview na Timeline\""),
      translateInterfaceText("Disponível apenas em vídeos novos — enviados a partir de agora"),
      translateInterfaceText("Pra liberar o recurso em um vídeo antigo, basta refazer o upload: o processamento gera o storyboard automaticamente"),
      translateInterfaceText("Requer que os controles do player estejam habilitados"),
    ]; },
    get footerNote() { return translateInterfaceText("Já está disponível na personalização dos seus novos vídeos."); },
  },
  {
    id: 'nov-2026-04-19-criar-sites-replic',
    type: 'novo_recurso',
    badge: 'Nova ferramenta',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-04-19',
    get title() { return translateInterfaceText("Criar Sites com a Replic"); },
    get description() { return translateInterfaceText("Conheça a Replic, a nossa ferramenta para criar e clonar sites de alta conversão em poucos minutos. Comece gratuitamente em apenas um clique, sem precisar cadastrar cartão de crédito, e ganhe velocidade para validar suas ofertas."); },
    get bullets() { return [
      translateInterfaceText("Crie sites do zero com blocos prontos e premium"),
      translateInterfaceText("Clone páginas existentes em minutos para acelerar testes"),
      translateInterfaceText("Editor visual intuitivo, sem necessidade de código"),
      translateInterfaceText("Publicação rápida com domínio próprio ou subdomínio"),
      translateInterfaceText("Teste grátis com um clique, sem cartão de crédito"),
    ]; },
    get footerNote() { return translateInterfaceText("Disponível agora no menu Ferramentas."); },
  },
  {
    id: 'nov-2026-04-19-quiz-funnelx',
    type: 'novo_recurso',
    badge: 'Nova ferramenta',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-04-19',
    get title() { return translateInterfaceText("Quiz Interativo com FunnelX"); },
    get description() { return translateInterfaceText("A FunnelX é a nossa ferramenta para criar quizzes interativos de alta performance. Engaje sua audiência, qualifique leads e direcione cada pessoa para a oferta certa, com lógica condicional e visual premium."); },
    get bullets() { return [
      translateInterfaceText("Construa quizzes em minutos com editor visual"),
      translateInterfaceText("Lógica condicional para personalizar a jornada de cada lead"),
      translateInterfaceText("Resultados segmentados que aumentam a conversão final"),
      translateInterfaceText("Integração nativa com VSLs e funis da Host VSL"),
      translateInterfaceText("Comece a usar direto pelo menu Ferramentas"),
    ]; },
    get footerNote() { return translateInterfaceText("Transforme curiosidade em conversão."); },
  },
  {
    id: 'nov-2026-04-19-asniper-espionar-ofertas',
    type: 'novo_recurso',
    badge: 'Nova ferramenta',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-04-19',
    get title() { return translateInterfaceText("Espionar Ofertas com a AdSniper"); },
    get description() { return translateInterfaceText("Com a AdSniper você espiona em tempo real as ofertas que estão escalando na Meta Ads e descobre rapidamente quais criativos, ângulos e funis estão convertendo. Use o cupom 50%OFF e garanta 50% de desconto em qualquer plano."); },
    get bullets() { return [
      translateInterfaceText("Monitore ofertas validadas que estão rodando na Meta agora"),
      translateInterfaceText("Descubra criativos, ângulos e funis vencedores"),
      translateInterfaceText("Filtros avançados por nicho, formato e tempo no ar"),
      translateInterfaceText("Inspiração direta para suas próximas VSLs e campanhas"),
      translateInterfaceText("Cupom exclusivo 50%OFF para 50% de desconto em qualquer plano"),
    ]; },
    get footerNote() { return translateInterfaceText("Use o cupom 50%OFF e comece hoje mesmo."); },
  },
  {
    id: 'nov-2026-04-19-mini-hooks',
    type: 'novo_recurso',
    badge: 'Novo recurso',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-04-19',
    get title() { return translateInterfaceText("Mini Hooks"); },
    get description() { return translateInterfaceText("Mini Hooks são pequenos blocos de texto que aparecem em momentos estratégicos do vídeo para reativar a atenção do espectador. Configure quantos quiser, defina o tempo de exibição e veja o impacto direto na retenção."); },
    get bullets() { return [
      translateInterfaceText("Adicione vários hooks com tempo de início e fim independentes"),
      translateInterfaceText("Edite texto e estilo direto no painel, com preview em tempo real"),
      translateInterfaceText("Animações suaves de entrada e saída sem quebrar a imersão"),
      translateInterfaceText("Combine com Botão de Ação e Headline para reforçar a oferta"),
      translateInterfaceText("Ative com um clique no painel de recursos do vídeo"),
    ]; },
    get footerNote() { return translateInterfaceText("Mantenha o público colado do início ao fim."); },
  },
  {
    id: 'nov-2026-04-19-academy',
    type: 'novo_recurso',
    badge: 'Novo recurso',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-04-19',
    get title() { return translateInterfaceText("Academy com tutoriais"); },
    get description() { return translateInterfaceText("A Academy chegou para acelerar seu domínio da plataforma. Trilha organizada por módulos, com aulas curtas e objetivas sobre cada recurso, do básico ao avançado, com progresso salvo automaticamente."); },
    get bullets() { return [
      translateInterfaceText("Trilha organizada por módulos: Primeiros passos, Personalização, Conversão, Análises e Plano"),
      translateInterfaceText("Progresso salvo por aula e por módulo, com indicadores visuais"),
      translateInterfaceText("Aulas curtas e objetivas com player premium integrado"),
      translateInterfaceText("Busca rápida por aula em qualquer módulo"),
      translateInterfaceText("Layout otimizado para mobile, tablet e desktop"),
    ]; },
    get footerNote() { return translateInterfaceText("Aprenda no seu ritmo, direto do menu Academy."); },
  },
  {
    id: 'nov-2026-04-10-funil-metricas',
    type: 'novo_recurso',
    badge: 'Novo recurso',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-04-10',
    get title() { return translateInterfaceText("Funil de Conversão nas Métricas"); },
    get description() { return translateInterfaceText("Acompanhe de forma visual e intuitiva cada etapa da jornada do espectador. O funil mostra desde os carregamentos da página até as conversões finais, com percentuais e valores absolutos para cada etapa."); },
    get bullets() { return [
      translateInterfaceText("Visualização em barras verticais proporcionais com gradiente azul progressivo"),
      translateInterfaceText("Cinco etapas do funil: Carregamentos, Visualizações Únicas, Plays Únicos, Cliques no Botão e Conversões"),
      translateInterfaceText("Taxas calculadas automaticamente: Play Rate, Click Rate e Conversion Rate"),
      translateInterfaceText("Efeito de foco ao passar o mouse: destaca a barra selecionada e suaviza as demais"),
      translateInterfaceText("Gráfico de Momento de Conversão no Vídeo para identificar os pontos de maior interesse"),
    ]; },
    get footerNote() { return translateInterfaceText("Entenda onde sua audiência cai e onde a conversão acontece."); },
  },
  {
    id: 'nov-2026-04-10-autoplay-customizar',
    type: 'novo_recurso',
    badge: 'Novo recurso',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-04-10',
    get title() { return translateInterfaceText("Personalizar Caixa do Autoplay"); },
    get description() { return translateInterfaceText("Agora você pode personalizar a caixa de autoplay de forma visual e intuitiva. Arraste para mover, use os handles para redimensionar e veja as alterações em tempo real no preview do player."); },
    get bullets() { return [
      translateInterfaceText("Arraste a caixa para qualquer posição dentro do player"),
      translateInterfaceText("Redimensione usando os handles nos cantos e laterais"),
      translateInterfaceText("Guias de snap para alinhamento preciso (centro, bordas)"),
      translateInterfaceText("Área segura visual para garantir visibilidade em todos os dispositivos"),
      translateInterfaceText("Todas as alterações refletem no embed final automaticamente"),
    ]; },
    get footerNote() { return translateInterfaceText("Controle total sobre a aparência do seu autoplay."); },
  },
  {
    id: 'nov-2026-04-09-exibicao-dispositivo',
    type: 'novo_recurso',
    badge: 'Novo recurso',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-04-09',
    get title() { return translateInterfaceText("Exibição por Dispositivo"); },
    get description() { return translateInterfaceText("Configure comportamentos específicos do player dependendo do dispositivo do espectador. Ofereça a melhor experiência em desktop, tablet e mobile de forma automática."); },
    get bullets() { return [
      translateInterfaceText("Personalize o player de forma independente para cada tipo de dispositivo"),
      translateInterfaceText("Otimize a experiência mobile sem comprometer o desktop"),
      translateInterfaceText("Ajustes automáticos de layout e interação por tela"),
      translateInterfaceText("Ative com um clique no painel de recursos do vídeo"),
    ]; },
    get footerNote() { return translateInterfaceText("Entregue a experiência ideal em qualquer tela."); },
  },
  {
    id: 'nov-2026-04-09-legendas',
    type: 'novo_recurso',
    badge: 'Novo recurso',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-04-09',
    get title() { return translateInterfaceText("Legendas"); },
    get description() { return translateInterfaceText("Adicione legendas aos seus vídeos com suporte a arquivos .VTT e .SRT. Edite textos e tempos diretamente no painel, reordene blocos e escolha entre 4 animações de exibição."); },
    get bullets() { return [
      translateInterfaceText("Upload de arquivos .VTT e .SRT com detecção automática de formato"),
      translateInterfaceText("Edição inline de texto e tempos diretamente no painel"),
      translateInterfaceText("Reordenação por drag-and-drop dos blocos de legenda"),
      translateInterfaceText("Quatro animações disponíveis: Nenhuma, Fade, Pop e Palavra por palavra"),
      translateInterfaceText("Layout responsivo de cápsula central com largura adaptativa por dispositivo"),
    ]; },
    get footerNote() { return translateInterfaceText("Acessibilidade e retenção em um só recurso."); },
  },
  {
    id: 'nov-2026-04-09-recomecar-video',
    type: 'novo_recurso',
    badge: 'Novo recurso',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-04-09',
    get title() { return translateInterfaceText("Recomeçar Vídeo Após o Fim"); },
    get description() { return translateInterfaceText("Configure o vídeo para retornar automaticamente ao início quando terminar. Com Autoplay ativo, entra em loop contínuo. Caso contrário, fica pausado no início pronto para ser assistido novamente."); },
    get bullets() { return [
      translateInterfaceText("Retorno automático ao segundo 0:00 ao término do vídeo"),
      translateInterfaceText("Loop contínuo quando combinado com Autoplay ativo"),
      translateInterfaceText("Sem replay automático se o Autoplay estiver desativado"),
      translateInterfaceText("Ative com um clique direto no painel de recursos"),
    ]; },
    get footerNote() { return translateInterfaceText("Ideal para vídeos de vitrine, demonstrações e VSLs em loop."); },
  },
  {
    id: 'nov-2026-03-25-pausa-inteligente',
    type: 'novo_recurso',
    badge: 'Novo recurso',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-03-25',
    get title() { return translateInterfaceText("Pausa Inteligente"); },
    get description() { return translateInterfaceText("Agora seus vídeos pausam automaticamente quando o espectador sai da aba do navegador. Ao retornar, a reprodução continua exatamente de onde parou, sem perder nenhum segundo de conteúdo."); },
    get bullets() { return [
      translateInterfaceText("Pausa automática ao detectar que o espectador saiu da aba"),
      translateInterfaceText("Retomada instantânea ao voltar, sem precisar clicar em nada"),
      translateInterfaceText("Respeita pausas manuais: só retoma se foi pausado pela função"),
      translateInterfaceText("Funciona com a Page Visibility API para máxima compatibilidade"),
      translateInterfaceText("Ative com um clique no painel de recursos do vídeo"),
    ]; },
    get footerNote() { return translateInterfaceText("Garanta que cada segundo do seu vídeo seja assistido."); },
  },
  {
    id: 'nov-2026-03-03-compartilhar-video',
    type: 'novo_recurso',
    badge: 'Novo recurso',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-03-03',
    get title() { return translateInterfaceText("Compartilhar Vídeo com Afiliados"); },
    get description() { return translateInterfaceText("Agora você pode compartilhar seus vídeos com afiliados por link. O afiliado copia o vídeo direto no painel com um clique, sem precisar baixar no Drive ou fazer upload demorado. Você acompanha quem copiou e toda a atividade do compartilhamento."); },
    get bullets() { return [
      translateInterfaceText("Gere um link compartilhável para qualquer vídeo original do seu painel"),
      translateInterfaceText("Afiliado copia o vídeo com um clique e já pode usar sem upload"),
      translateInterfaceText("Acompanhe cópias por dia, top estados e histórico completo de eventos"),
      translateInterfaceText("Controle o acesso com toggle de ativação, limite de cópias e data de expiração"),
      translateInterfaceText("Exporte o histórico de eventos em CSV para seus relatórios"),
      translateInterfaceText("Vídeos copiados ficam protegidos e não podem ser compartilhados novamente"),
    ]; },
    get footerNote() { return translateInterfaceText("Distribua seus vídeos com velocidade e controle. Menos etapas, mais escala para sua operação com afiliados."); },
    image: compartilharVideoBanner,
  },
  {
    id: 'nov-2026-03-03-picos-horario',
    type: 'novo_recurso',
    badge: 'Novo recurso',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-03-03',
    get title() { return translateInterfaceText("Picos de Horário nas Métricas do Vídeo"); },
    get description() { return translateInterfaceText("Chegou o recurso Picos de Horário. Agora você identifica com clareza quando seu público mais assiste, em quais dias e em quais faixas de horário. Isso permite publicar com mais precisão, otimizar suas campanhas e aumentar o desempenho do vídeo com base em dados reais."); },
    get bullets() { return [
      translateInterfaceText("Descubra o horário campeão do período com o melhor volume de plays e visualizações"),
      translateInterfaceText("Compare horários recomendados com ranking de melhores faixas para repetir o que já funciona"),
      translateInterfaceText("Veja os horários ideais por tipo de acesso e entenda diferenças entre desktop, mobile e tablet"),
      translateInterfaceText("Analise o mapa de calor por dia e hora para enxergar padrões de audiência em segundos"),
      translateInterfaceText("Receba um insight automático com leitura rápida do comportamento do público"),
      translateInterfaceText("Exporte os dados em CSV para usar em relatórios e rotinas de otimização"),
    ]; },
    get footerNote() { return translateInterfaceText("Use Picos de Horário para alinhar postagem, disparos e tráfego pago com os momentos de maior atenção. Menos achismo, mais performance."); },
    image: picosHorarioBanner,
  },
  {
    id: 'nov-2026-02-24-front-v2',
    type: 'novo_front',
    badge: 'Novo front',
    badgeColor: 'bg-[#4B7BFF]/10 text-[#4B7BFF]',
    date: '2026-02-24',
    get title() { return translateInterfaceText("Novo Front Host VSL 2.0"); },
    get description() { return translateInterfaceText("A Host VSL entrou em uma nova fase. O Front 2.0 foi criado para quem quer operar com mais clareza, mais velocidade e mais controle. Tudo ficou mais consistente, mais fluido e pronto para acompanhar o crescimento de quem escala vendas todos os dias."); },
    get bullets() { return [
      translateInterfaceText("Visual premium e consistente em toda a plataforma"),
      translateInterfaceText("Navegação mais rápida e responsiva em qualquer tela"),
      translateInterfaceText("Experiência mais clara para reduzir erros e acelerar decisões"),
      translateInterfaceText("Componentes e modais com padrão unificado do novo front"),
      translateInterfaceText("Base preparada para liberar novas funcionalidades com mais velocidade"),
    ]; },
    get footerNote() { return translateInterfaceText("Essa atualização deixa a Host VSL ainda mais alinhada com quem busca escala, performance e operação profissional."); },
    image: frontV2Banner,
  },
];

const FILTER_OPTIONS = [
  { value: 'todas', get label() { return translateInterfaceText("Todas"); } },
  { value: 'novo_recurso', get label() { return translateInterfaceText("Novo recurso"); } },
];

function FilterDropdown({ value, options, onChange }: {
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}) {
  useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = options.find(o => o.value === value);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          "h-10 px-3 pr-2 rounded-[10px] text-sm text-foreground cursor-pointer flex items-center gap-2",
          "bg-white border border-[#E8EEF7]",
          "hover:border-[#4B7BFF]/40",
          "transition-all duration-150",
          open && "border-[#4B7BFF] shadow-[0_0_0_3px_rgba(75,123,255,0.12)]"
        )}
      >
        <span className="whitespace-nowrap">{selected?.label}</span>
        <ChevronDown className={cn("h-4 w-4 text-muted-foreground/60 transition-transform duration-150", open && "rotate-180")} />
      </button>
      {open && (
        <div
          className="absolute right-0 top-[calc(100%+6px)] z-[60] min-w-[160px] rounded-xl bg-white border border-[#E8EEF7] py-1.5 animate-in fade-in-0 zoom-in-95 duration-150"
          style={{ boxShadow: '0 8px 24px rgba(15, 23, 42, 0.10), 0 2px 6px rgba(15, 23, 42, 0.04)' }}
        >
          {options.map((opt) => (
            <button
              key={opt.value}
              onClick={() => { onChange(opt.value); setOpen(false); }}
              className={cn(
                "w-full flex items-center justify-between px-3.5 py-2 text-[14px] transition-colors duration-100",
                value === opt.value
                  ? "text-[#4B7BFF] font-semibold bg-[#4B7BFF]/5"
                  : "text-foreground hover:bg-[#F5F7FA]"
              )}
            >
              <span>{opt.label}</span>
              {value === opt.value && <Check className="h-4 w-4 text-[#4B7BFF]" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function formatDate(dateStr: string) {
  const d = new Date(dateStr + 'T00:00:00');
  const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  return `${d.getDate().toString().padStart(2, '0')} ${months[d.getMonth()]}, ${d.getFullYear()}`;
}

function HelpCTA() {
  useLanguage();
  const handleOpenCrisp = () => {
    try {
      window.$crisp?.push(["do", "chat:open"]);
    } catch (e) {
      console.log('Crisp não disponível');
    }
  };

  return (
    <div className="mt-6 pt-5 border-t border-[#EEF2F8]">
      <div
        className="flex items-center gap-3 p-3 sm:gap-4 sm:p-4 rounded-xl"
        style={{
          background: 'rgba(75, 123, 255, 0.04)',
          border: '1px solid rgba(75, 123, 255, 0.10)',
        }}
      >
        <div className="flex-shrink-0 h-7 w-7 sm:h-9 sm:w-9 rounded-lg bg-[#4B7BFF]/10 flex items-center justify-center">
          <MessageCircle className="h-[15px] w-[15px] sm:h-[18px] sm:w-[18px] text-[#4B7BFF]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[12px] sm:text-[14px] font-semibold text-foreground leading-snug">{translateInterfaceText("Ainda tem dúvidas")}</p>
          <p className="text-[11px] sm:text-[13px] text-muted-foreground/70 leading-snug mt-0.5">{translateInterfaceText("Fale com nossa equipe e resolva em poucos minutos.")}</p>
        </div>
        <button
          onClick={handleOpenCrisp}
          className={cn(
            "flex-shrink-0 h-7 sm:h-9 px-3 sm:px-4 rounded-lg text-[11px] sm:text-[13px] font-medium",
            "bg-[#4B7BFF] text-white",
            "hover:bg-[#3A6AEE] active:bg-[#2F5CE0]",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4B7BFF]/30",
            "transition-colors duration-150"
          )}
        >
          {translateInterfaceText("Chamar no chat")}</button>
      </div>
    </div>
  );
}

function NovidadeCard({ novidade }: { novidade: Novidade }) {
  useLanguage();
  const [imageLoaded, setImageLoaded] = useState(false);

  return (
    <article className="overflow-hidden space-y-3">
      {/* Badge + Date */}
      <div className="flex items-baseline gap-3">
        <span className={cn(
          'px-3 py-[3px] text-[11px] font-semibold rounded-full leading-none',
          novidade.badgeColor
        )}>
          {novidade.badge}
        </span>
        <span className="text-[12px] text-muted-foreground/60 leading-none">{formatDate(novidade.date)}</span>
      </div>

      {/* Title */}
      <h3 className="text-[18px] font-semibold text-foreground leading-snug">{novidade.title}</h3>

      {/* Image */}
      {novidade.image && (
        <div
          className="relative aspect-video w-full overflow-hidden rounded-[14px]"
          style={{
            border: '1.5px solid rgba(45, 127, 249, 0.35)',
            boxShadow: '0 6px 18px rgba(45, 127, 249, 0.10)',
          }}
        >
          {!imageLoaded && (
            <Skeleton className="absolute inset-0 w-full h-full rounded-none" />
          )}
          <img
            src={novidade.image}
            alt={novidade.title}
            loading="lazy"
            onLoad={() => setImageLoaded(true)}
            className={cn(
              'w-full h-full object-cover transition-opacity duration-300',
              imageLoaded ? 'opacity-100' : 'opacity-0'
            )}
          />
        </div>
      )}

      {/* Description */}
      <p className="text-[14px] text-muted-foreground leading-[1.6]">{novidade.description}</p>

      {/* Bullets */}
      {novidade.bullets.length > 0 && (
        <ul className="space-y-2 pt-1">
          {novidade.bullets.map((bullet, i) => (
            <li key={i} className="flex items-start gap-2.5 text-[14px] text-foreground/80 leading-[1.5]">
              <span className="mt-[7px] h-[5px] w-[5px] rounded-full bg-[#4B7BFF] flex-shrink-0" />
              <span>{bullet}</span>
            </li>
          ))}
        </ul>
      )}

      <NovidadeSections sections={novidade.sections} />

      {/* Footer note */}
      {novidade.footerNote && (
        <p className="text-[13px] text-muted-foreground/70 italic pt-1 leading-[1.5]">{novidade.footerNote}</p>
      )}

      <HelpCTA />
    </article>
  );
}

function NovidadesSkeleton() {
  useLanguage();
  return (
    <div className="space-y-6">
      {[1, 2, 3].map((i) => (
        <div key={i} className="overflow-hidden">
          <Skeleton className="aspect-video w-full rounded-[14px]" />
          <div className="pt-5 space-y-3">
            <div className="flex items-center gap-3">
              <Skeleton className="h-5 w-20 rounded-full" />
              <Skeleton className="h-4 w-24" />
            </div>
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
          </div>
        </div>
      ))}
    </div>
  );
}

interface NovidadesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NovidadesModal({ open, onOpenChange }: NovidadesModalProps) {
  useLanguage();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('todas');
  const [isLoading] = useState(false);

  const filtered = useMemo(() => {
    return NOVIDADES_DATA.filter((n) => {
      if (filter !== 'todas' && n.type !== filter) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const inTitle = n.title.toLowerCase().includes(q);
        const inDesc = n.description.toLowerCase().includes(q);
        const inBullets = n.bullets.some((b) => b.toLowerCase().includes(q));
        const inSections = n.sections?.some((section) => [section.title, ...(section.paragraphs || []), ...(section.steps || [])].some((text) => text.toLowerCase().includes(q)));
        if (!inTitle && !inDesc && !inBullets && !inSections) return false;
      }
      return true;
    });
  }, [search, filter]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-[9999] translate-x-[-50%] translate-y-[-50%]",
            "flex flex-col overflow-hidden bg-background border-0",
            "duration-200",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          )}
          style={{
            width: 'min(680px, calc(100vw - 24px))',
            maxWidth: 'min(680px, calc(100vw - 24px))',
            maxHeight: '90vh',
            borderRadius: '16px',
            boxShadow: '0 24px 64px rgba(15, 23, 42, 0.12), 0 2px 8px rgba(15, 23, 42, 0.06)',
          }}
        >
          {/* Header — gradiente azul */}
          <div
            className="flex items-center justify-between px-[22px] flex-shrink-0"
            style={{
              height: '60px',
              background: 'linear-gradient(135deg, #4B7BFF 0%, #3A6AEE 100%)',
            }}
          >
            <div className="flex items-center gap-2.5">
              <Megaphone className="w-5 h-5 text-white/90" />
              <DialogTitle className="text-[17px] font-semibold text-white leading-tight tracking-[-0.01em]">
                {translateInterfaceText("Novidades")}</DialogTitle>
              <DialogDescription className="sr-only">{translateInterfaceText("Novos recursos, melhorias e orientações para usar a HostVSL.")}</DialogDescription>
            </div>
            <button
              onClick={() => onOpenChange(false)}
              className="h-9 w-9 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors"
              aria-label={translateInterfaceText("Fechar")}
            >
              <X className="h-[18px] w-[18px] text-white" />
            </button>
          </div>

          {/* Search & Filter */}
          <div className="flex items-center gap-3 px-[22px] py-3 border-b border-[#EDF1F7] bg-background flex-shrink-0">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/50" />
              <input
                type="text"
                placeholder={translateInterfaceText("Pesquisar novidades...")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={cn(
                  "w-full h-10 pl-9 pr-4 rounded-[10px] text-sm text-foreground placeholder:text-muted-foreground/50",
                  "bg-white border border-[#E8EEF7]",
                  "outline-none focus-visible:outline-none",
                  "focus:border-[#4B7BFF] focus:shadow-[0_0_0_3px_rgba(75,123,255,0.12)]",
                  "transition-all duration-150"
                )}
              />
            </div>
            <FilterDropdown
              value={filter}
              options={FILTER_OPTIONS}
              onChange={setFilter}
            />
          </div>

          {/* Feed body — scrollable */}
          <div className="flex-1 overflow-y-auto px-[22px] py-5 min-h-0">
            {isLoading ? (
              <NovidadesSkeleton />
            ) : filtered.length === 0 ? (
              <div className="text-center py-16">
                <Sparkles className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
                <h3 className="text-[17px] font-semibold text-foreground mb-1">{translateInterfaceText("Nenhuma novidade por aqui")}</h3>
                <p className="text-[13px] text-muted-foreground">
                  {translateInterfaceText("Quando tivermos atualizações, elas aparecem nesta tela.")}</p>
              </div>
            ) : (
              <div className="divide-y divide-[#EEF2F8]">
                {filtered.map((n, idx) => (
                  <div key={n.id} className={cn(idx > 0 && "pt-[20px]", idx < filtered.length - 1 && "pb-[20px]")}>
                    <NovidadeCard novidade={n} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
